import { FC, useRef } from "react";
import { Button } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { RoutesMap } from "../../../../../routes.tsx";
import { newAuthRequest } from "../../../../../api/newAuthorization";
import { clearAuthorization } from "../../../../../utils/session/clearAuthorization";
import { usePersistStore } from "../../../../../store/persistStore.ts";
import { useLoginFormStore } from "../../../../../store/loginFormStore.ts";
import { isNewAuthorization, useAuthorizationStore } from "../../../../../store/authorizationStore";

export const ProfilePageLogout: FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const clearToken = usePersistStore(state => state.clearJwt);
  const clearDeviceToken = usePersistStore(state => state.clearDeviceJwt);
  const loginFormStore = useLoginFormStore.getState();
  const logoutPending = useRef(false);

  const handleNewLogout = async () => {
    if (logoutPending.current) {
      return;
    }

    logoutPending.current = true;
    const { token, deviceId, epoch } = useAuthorizationStore.getState();
    const controller = new AbortController();
    let timeout: ReturnType<typeof setTimeout> | undefined;

    try {
      if (token && deviceId) {
        await Promise.race([
          newAuthRequest("/token/logout", { deviceId }, "POST", token, controller.signal),
          new Promise<void>((resolve) => {
            timeout = setTimeout(() => {
              controller.abort();
              resolve();
            }, 1000);
          }),
        ]);
      }
    } catch (_error) { /* Local logout is unconditional. */ } finally {
      clearTimeout(timeout);
      logoutPending.current = false;

      if (useAuthorizationStore.getState().epoch === epoch && isNewAuthorization()) {
        clearAuthorization("keycloak");
        navigate(RoutesMap.login.index, { replace: true });
      }
    }
  };

  const handleOldLogout = () => {
    clearToken();
    clearDeviceToken();
    queryClient.clear();
    // Clear login form state (phone number, SMS, passport)
    loginFormStore.clearPhoneForm();
    loginFormStore.clearSMSForm();
    loginFormStore.clearForm();

    sessionStorage.clear();
    navigate(RoutesMap.login.index);
  };

  const handleLogout = () => {
    if (isNewAuthorization()) {
      void handleNewLogout();
      return;
    }

    handleOldLogout();
  };

  return (
    <Button w="100%" mt="44px" onClick={handleLogout}>
      Выйти
    </Button>
  );
};
